import { useCallback, useEffect, useMemo, useState } from 'react';
import type { EntryRow, Totals, WorkType } from '../../shared/types';
import { WORK_TYPES } from '../../shared/types';
import { api } from '../lib/api';
import { useStore } from '../lib/store';
import { buildPeriod, todayISO, type Period } from '../lib/period';
import type { ExportRow } from '../lib/export';
import { PageHeader } from '../components/AppShell';
import { PeriodSwitcher } from '../components/PeriodSwitcher';
import { TimesheetTable } from '../components/TimesheetTable';
import { ExportDialog } from '../components/ExportDialog';
import { EmptyState, ErrorBanner, Spinner } from '../components/ui';
import { entryPayload } from '../components/EntryFormRow';

const EMPTY_TOTALS: Totals = { minutes: 0, amount_inr: 0, entry_count: 0 };

const toExportRows = (entries: EntryRow[]): ExportRow[] =>
  entries.map((e) => ({
    work_date: e.work_date,
    project: e.project_name,
    title: e.title,
    work_type: e.work_type,
    minutes: e.minutes,
    remark: e.remark,
    amount: e.amount_inr,
  }));

export function Timesheet() {
  const { activeProjects, projects, weekStartsOn } = useStore();

  const [period, setPeriod] = useState<Period>(() => buildPeriod('week', new Date(), weekStartsOn));
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [entries, setEntries] = useState<EntryRow[]>([]);
  const [totals, setTotals] = useState<Totals>(EMPTY_TOTALS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const query = useMemo(
    () => ({
      from: period.from,
      to: period.to,
      projectIds: projectFilter ? [projectFilter] : undefined,
      types: typeFilter ? [typeFilter] : undefined,
    }),
    [period.from, period.to, projectFilter, typeFilter],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listEntries(query);
      setEntries(data.entries);
      setTotals(data.totals);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load entries');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Wraps a mutation so every path refreshes totals and surfaces failures. */
  const mutate = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const scopeName = projectFilter
    ? (projects.find((p) => p.id === projectFilter)?.name ?? 'all')
    : 'all';

  if (activeProjects.length === 0) {
    return (
      <>
        <PageHeader title="Timesheet" />
        <div className="card">
          <EmptyState
            title="Create a project first"
            description="Every timesheet row belongs to a project, and the project's hourly rate is what turns your hours into rupees."
            action={
              <a href="/projects" className="btn btn-primary">
                Add a project
              </a>
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Timesheet"
        subtitle={`${period.label} · ${totals.entry_count} ${totals.entry_count === 1 ? 'entry' : 'entries'}`}
        actions={
          <button type="button" className="btn btn-secondary" onClick={() => setExporting(true)}>
            Export
          </button>
        }
      />

      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <PeriodSwitcher period={period} weekStartsOn={weekStartsOn} onChange={setPeriod} />

        <div className="flex flex-wrap items-center gap-2">
          <select
            className="field !w-auto !py-1.5 text-[13px]"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            aria-label="Filter by project"
          >
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            className="field !w-auto !py-1.5 text-[13px]"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as WorkType | '')}
            aria-label="Filter by type of work"
          >
            <option value="">All types</option>
            {WORK_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
        </div>
      )}

      {loading ? (
        <div className="card">
          <Spinner label="Loading entries" />
        </div>
      ) : (
        <TimesheetTable
          entries={entries}
          projects={activeProjects}
          totals={totals}
          showAmounts
          groupByMonth={period.mode === 'year'}
          defaultDate={todayISO()}
          defaultProjectId={projectFilter || (activeProjects[0]?.id ?? '')}
          busy={busy}
          onCreate={(draft) => mutate(() => api.createEntry(entryPayload(draft)))}
          onUpdate={(id, draft) => mutate(() => api.updateEntry(id, entryPayload(draft)))}
          onDelete={(entry) => mutate(() => api.deleteEntry(entry.id))}
        />
      )}

      {exporting && (
        <ExportDialog
          from={period.from}
          to={period.to}
          onClose={() => setExporting(false)}
          source={{
            scopeName,
            showAmounts: true,
            load: async (from, to) => {
              const data = await api.listEntries({ ...query, from, to });
              return {
                rows: toExportRows(data.entries),
                totals: { minutes: data.totals.minutes, amount: data.totals.amount_inr },
              };
            },
          }}
        />
      )}
    </>
  );
}
